import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleModal } from 'apps/auth/actions/appAction';
import { updateFieldValues, updateFormProperties } from 'apps/auth/actions/formAction';
import { BRAZE_BANNER_PLACEMENTS } from 'apps/auth/analytics/braze';
import BrazeBannerPlacement from 'apps/auth/components/braze/BrazeBannerPlacement';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import SuggestionModal from 'apps/auth/components/common/Modal/SuggestionModal';
import GiftBlock from 'apps/auth/components/common/send/deliveryOptions/GiftBlock/GiftBlock';
import FeeTextPopup from 'apps/auth/components/common/send/popups/FeeTextPopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import { transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import { FEE_INFO_CLICKED, SENDING_METHOD_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import {
    PAYMENT_COUNTRY,
    PAYMENT_CURRENCY,
    PAYMENT_METHOD_CATEGORY,
    PAYOUT_COUNTRY,
    PAYOUT_CURRENCY,
    PAYOUT_METHOD,
    SCREEN,
} from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { DELIVERY_METHOD_LIST } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { BRAZE_IAM_PLACEMENTS, BRAZE_IAM_SCREEN_EVENTS } from 'apps/auth/constants/braze/brazeIam';
import { PAYMENT_ACH } from 'apps/auth/constants/Constants';
import { AMOUNT_FROM, DELIVERY_METHOD } from 'apps/auth/constants/fieldsName';
import { SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, useForm, usePageMessages, useTypeOfTransfer } from 'apps/auth/hooks/selectors';
import { useBrazeIamScreen } from 'apps/auth/hooks/useBrazeIamScreen';
import { GTPaymentMethodService } from 'apps/auth/services/GTPaymentMethodService';
import { showPromoModal } from 'apps/auth/services/promoModalService';

import RenderPaymentMethod from './RenderPaymentMethod';
import PaymentStoriesModal from '../modals/PaymentStoriesModal';

export const getIconName = (methodName) => {
    const iconMap = {
        BANK_ACCOUNT: 'bank',
        LEAN: 'bank-zap',
        MYBANK: 'bank-zap',
        FLINKS: 'bank-plus',
        INTERAC: 'interac',
        PEOPLE_TRUST_INTERAC: 'bank-zap',
        SOFORT: 'bank-zap',
        KLARNA: 'klarna',
        TRUSTLY: 'bank-zap',
        TINK: 'bank-zap',
        GIROPAY: 'bank-zap',
        IDEAL: 'bank-zap',
        BLIK: 'bank-zap',
        EPS: 'bank-zap',
        MBWAY: 'bank-zap',
        BONUS: 'gift',
        MODULR: 'bank',
        BANK_CARD: 'card',
        ACH: 'bank-plus',
        PLAID: 'bank-zap',
        GLOBAL_ACCOUNT: 'bank-zap',
    };
    const icon = iconMap[methodName];
    if (!icon) {
        return 'bank';
    }
    return icon;
};

const GTPaymentMethods = ({ isGlobalFlow = false, ...props }) => {
    const { ballId } = useAuth();
    const extra = useExtra();
    const {
        feedbackFormAvailable,
        toCountry = {},
        toCurrency = {},
        availablePaymentMethodsList = {},
        paymentMethodsOrder = {},
        authPaymentMethodsPromoAction,
        canShowAmlLevelUpgrade,
        canShowOowFlow,
        fromCountry = {},
        fromCurrency = {},
        dcSources = {},
        sendFlowV3,
        userCountry,
        incompleteTransfer,
    } = extra;
    const { suggestedButton, paymentMethodTitle, paymentMethodNewTitle, addNewAch } = usePageMessages();
    const { fields = {} } = useForm(props.formName);
    const paymentMethodService = useRef(null);
    const deliveryMethod = fields?.[DELIVERY_METHOD]?.value;
    const amountFrom = fields?.[AMOUNT_FROM]?.value;
    const fromCurrencyCode = fromCurrency?.code;
    const isShowPaymentMethods = Object.keys(availablePaymentMethodsList).length > 0 && paymentMethodsOrder?.sourceOptionsGroups?.length > 0;
    const typeOfTransfer = useTypeOfTransfer();

    useBrazeIamScreen(BRAZE_IAM_SCREEN_EVENTS.PAYMENT_OPTIONS, BRAZE_IAM_PLACEMENTS.PAYMENT_OPTIONS);

    useEffect(() => {
        paymentMethodService.current = new GTPaymentMethodService(
            props.updateFieldValues,
            props.updateFormProperties,
            props.formName,
            toCountry?.code,
            toCurrency?.code,
            deliveryMethod,
            typeOfTransfer,
            incompleteTransfer,
        );
        if (isGlobalFlow) {
            paymentMethodService.current.setIsGlobalFlow();
        }
    }, [deliveryMethod, isGlobalFlow, props.formName, props.updateFieldValues, props.updateFormProperties, toCountry?.code, toCurrency?.code]);

    useEffect(() => {
        if (__isBrowser__) {
            props.sendAmplitudeEventOnRender?.(SENDING_METHOD_SCREEN_SHOWN);
        }
        props.paymentMethodState.current = { extra, fields };
    }, []);

    const openSuggestionPopup = (e) => {
        e.preventDefault();
        props.toggleModal(true, <SuggestionModal funding />);
    };

    const clickHandler = (e, method, code, category) => {
        const sendMarketingSliders = availablePaymentMethodsList[category]?.sendMarketingStories;
        const isNotDCType = !code?.includes('type=DC');

        e.preventDefault();
        if (sendMarketingSliders && Object.keys(sendMarketingSliders)?.length > 0 && isNotDCType) {
            const showModal = showPromoModal(ballId, sendMarketingSliders, 'payment_marketing_info');
            if (showModal) {
                props.popupFullwidthRef.current?.open(
                    <PaymentStoriesModal
                        isMarketing={true}
                        sliders={sendMarketingSliders}
                        submitRef={props.submitRef}
                        popupFullwidthRef={props.popupFullwidthRef}
                        formName={SEND_FORM_NAME}
                        changePaymentMethod={() => {
                            paymentMethodService.current.changePaymentMethod(method, code, () => {
                                props.submitRef.current.click();
                            });
                        }}
                    />,
                );
            }
        } else {
            paymentMethodService.current.changePaymentMethod(method, code, () => {
                props.submitRef.current.click();
            });
        }
    };

    const getMethod = (category) => {
        const method = availablePaymentMethodsList[category];
        if (method) {
            let paymentMethod = method;
            if (paymentMethod?.method === PAYMENT_ACH) {
                paymentMethod = { ...method, name: addNewAch };
            }
            return paymentMethod;
        }
        return false;
    };
    const getRenderPaymentMethod = () => {
        if (isShowPaymentMethods) {
            const filterArrGroups = Object.values(paymentMethodsOrder?.sourceOptionsGroups).filter(({ paymentOptionsItems }) => {
                return paymentOptionsItems.some(({ category }) => {
                    return availablePaymentMethodsList.hasOwnProperty(category) && availablePaymentMethodsList[category];
                });
            });
            const sortGroupsArr = filterArrGroups.sort((a, b) => a.orderId - b.orderId);
            return sortGroupsArr.map(({ description, technicalDescription, paymentOptionsItems, feeInfo, feeInfoAdditional }, index, array) => {
                const sortedMethodsArr = paymentOptionsItems.sort((a, b) => a.order - b.order);
                return (
                    <div key={`${description}-${index}`} data-testid="gt-payment-methods-sorted">
                        {sendFlowV3 ? (
                            <Separator
                                isSubheader
                                marginTop="8px"
                                marginBottom="8px"
                                leftText={description}
                                rightText={feeInfo}
                                hasRightTextInfo={!!feeInfoAdditional}
                                onClick={() => {
                                    amplitude.getInstance().logEvent(FEE_INFO_CLICKED, {
                                        [SCREEN]: DELIVERY_METHOD_LIST,
                                        [PAYOUT_COUNTRY]: toCountry?.nameEn,
                                        [PAYOUT_CURRENCY]: toCurrency?.code,
                                        [PAYOUT_METHOD]: transactionMethods[deliveryMethod],
                                        [PAYMENT_COUNTRY]: fromCountry?.nameEn,
                                        [PAYMENT_CURRENCY]: fromCurrency?.code,
                                        [PAYMENT_METHOD_CATEGORY]: technicalDescription,
                                    });
                                    props.popupRef.current?.open(<FeeTextPopup text={feeInfoAdditional} isShowTerms={false} popupRef={props.popupRef} />);
                                }}
                            />
                        ) : (
                            <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                                {description}
                            </Text>
                        )}
                        {sortedMethodsArr.map(({ category, badge, actionBanner }, i) => {
                            const paymentMethod = getMethod(category);
                            return paymentMethod ? (
                                <RenderPaymentMethod
                                    key={`${i}-${category}`}
                                    category={category}
                                    userCountryId={userCountry?.id}
                                    paymentMethod={paymentMethod}
                                    formName={props.formName}
                                    returnToCalculator={props.returnToCalculator}
                                    modalRef={props.modalRef}
                                    clickHandler={clickHandler}
                                    dcSources={dcSources}
                                    submitRef={props.submitRef}
                                    canShowAmlLevelUpgrade={canShowAmlLevelUpgrade}
                                    canShowOowFlow={canShowOowFlow}
                                    icon={getIconName(paymentMethod?.method)}
                                    badge={badge}
                                    actionBanner={actionBanner}
                                    getPaymentFlow={props.getPaymentFlow}
                                    recipientType={props.typeOfTransfer}
                                    paymentMethodCategory={technicalDescription}
                                    toCountryCode={props.toCountryCode}
                                    toCurrencyCode={props.toCurrencyCode}
                                    isIncomplete={props.isIncomplete}
                                    popupRef={props.popupRef}
                                />
                            ) : null;
                        })}
                        {index !== array.length - 1 && !sendFlowV3 ? <Separator /> : null}
                    </div>
                );
            });
        }
        return null;
    };

    return (
        <>
            <HeaderPanel
                title={
                    <DangerouslyHtml
                        dangerouslyHtml={resolveArguments(
                            sendFlowV3 ? paymentMethodNewTitle : paymentMethodTitle,
                            sendFlowV3 ? [fromCurrencyCode] : [amountFrom, fromCurrencyCode],
                        )}
                    />
                }
            />
            {authPaymentMethodsPromoAction && <GiftBlock promoAction={authPaymentMethodsPromoAction} />}
            {getRenderPaymentMethod()}
            <BrazeBannerPlacement placementId={BRAZE_BANNER_PLACEMENTS.PAYMENT_OPTIONS} marginTop="24px" />
            {feedbackFormAvailable?.funding === 'true' && (
                <Button type="button" size="tight" variant="row" caption={suggestedButton} negativeMargin onClick={openSuggestionPopup} />
            )}
        </>
    );
};

export default connect(null, {
    updateFieldValues,
    updateFormProperties,
    toggleModal,
})(GTPaymentMethods);
